import { LoaderCircle, Pencil, Plus, X } from 'lucide-react';
import React, { useRef, useState } from 'react';
import Docs from '../Icons/Docs';
import Pdf from '../Icons/Pdf';
import Document from '../Icons/Document';
import { normalizeUrl } from '@univdiam/ui/lib/UrlNormalize';
import { FaVideo } from 'react-icons/fa';
import { RiFileExcel2Fill } from "react-icons/ri";

type MediaUploaderProps = {
  handleSendToUpload: (files: File[]) => Promise<void> | void;
  onRemove?: (index: number) => void;
  value: string[] | string;
  mediaLoading?: boolean;
};

export const getFileIcon = (url: string) => {
  const extension = url.split('.').pop()?.toLowerCase() || '';

  if (['png', 'jpg', 'jpeg', 'gif', 'bmp', 'svg', 'webp'].includes(extension)) {
    return (
      <img
        src={normalizeUrl(url)}
        alt="uploaded"
        className="w-full h-full object-cover rounded-[4px]"
      />
    );
  }

  const iconWrapper = (icon: React.ReactNode) => (
    <div className="w-6 h-6 [&>svg]:w-full [&>svg]:h-full [&>svg]:block">
      {icon}
    </div>
  );

  if (['pdf'].includes(extension)) return iconWrapper(<Pdf />);
  if (['doc', 'docx'].includes(extension)) return iconWrapper(<Docs />);
  if (['xls', 'xlsx'].includes(extension)) return iconWrapper(<RiFileExcel2Fill />);
  if (['mp4', 'mov', 'avi', 'webm'].includes(extension)) return iconWrapper(<FaVideo />);
  return iconWrapper(<Document />);
};

const MediaUploader = ({ handleSendToUpload, onRemove, value, mediaLoading = false }: MediaUploaderProps) => {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [localLoading, setLocalLoading] = useState(false);

  const isLoading = mediaLoading || localLoading;

  const handleFiles = async (selectedFiles: FileList | null) => {
    if (!selectedFiles) return;
    const newFiles = Array.from(selectedFiles);
    setLocalLoading(true);
    try {
      await handleSendToUpload(newFiles);
    } finally {
      setLocalLoading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const files = e.dataTransfer.files;
    handleFiles(files);
  };

  const isArray = Array.isArray(value);
  const mediaUrls = isArray ? value : value ? [value] : [];

  return (
    <div
      className={`w-full h-full border border-[#DBDBDB] rounded-[6px] p-[16px] relative ${isDragging ? 'bg-[#F6F6F6]' : 'bg-[#F6F6F680]'} ${mediaUrls.length ? 'cursor-pointer' : ''}`}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      {/* --- SINGLE FILE CASE --- */}
      {mediaUrls.length === 1 && !isArray ? (
        <div className="w-full h-full relative overflow-hidden">
          <img
            onClick={() => window.open(normalizeUrl(value as string), '_blank')}
            src={normalizeUrl(value as string)}
            alt="Uploaded"
            className="w-full h-fit object-cover rounded-[4px]"
          />

          {isLoading ? (
            <LoaderCircle className="absolute top-2 right-2 animate-spin text-white bg-black/50 rounded-full p-1" />
          ) : (
            <div className="absolute top-2 right-2 flex gap-1">
              {onRemove && (
                <div
                  onClick={(e) => {
                    e.stopPropagation();
                    onRemove(0);
                  }}
                  className="bg-black/60 text-white w-8 h-8 flex items-center justify-center rounded cursor-pointer hover:bg-black/70 transition"
                >
                  <X className="w-4 h-4" />
                </div>
              )}
              <div
                onClick={(e) => {
                  e.stopPropagation();
                  fileInputRef.current?.click();
                }}
                className="bg-black/60 text-white w-8 h-8 flex items-center justify-center rounded cursor-pointer hover:bg-black/70 transition"
              >
                <Pencil className="w-4 h-4" />
              </div>
            </div>
          )}

          {/* Hidden input for replace */}
          <input
            ref={fileInputRef}
            type="file"
            className="hidden"
            onChange={(e) => handleFiles(e.target.files)}
          />
        </div>
      ) : mediaUrls.length > 0 ? (
        /* --- MULTIPLE FILES CASE --- */
        <div className="flex gap-[8px] flex-wrap">
          {mediaUrls.map((url, idx) => (
            <div
              key={idx}
              className="relative w-[52px] h-[52px] flex items-center justify-center rounded-[4px] border border-[#E9EAEB] overflow-hidden group"
            >
              <div
                onClick={() => window.open(normalizeUrl(url as string), '_blank')}
                className="w-full h-full cursor-pointer hover:opacity-90 transition"
              >
                {getFileIcon(url)}
              </div>
              {onRemove && (
                <div
                  onClick={(e) => {
                    e.stopPropagation();
                    onRemove(idx);
                  }}
                  className="absolute top-1 right-1 bg-red-500 text-white w-4 h-4 flex items-center justify-center rounded-full cursor-pointer opacity-0 group-hover:opacity-100 transition"
                >
                  <X className="w-2.5 h-2.5" />
                </div>
              )}
            </div>
          ))}

          {/* Add more button */}
          {isLoading ? (
            <div className="w-[52px] h-[52px] rounded-[4px] border border-[#DBDBDB] flex items-center justify-center">
              <LoaderCircle className="animate-spin" />
            </div>
          ) : (
            <div
              onClick={(e) => {
                e.stopPropagation();
                fileInputRef.current?.click();
              }}
              className="w-[52px] h-[52px] rounded-[4px] border border-[#DBDBDB] flex items-center justify-center cursor-pointer hover:bg-[#f0f0f0] transition"
            >
              <Plus />
            </div>
          )}

          {/* Hidden input */}
          <input
            ref={fileInputRef}
            type="file"
            multiple
            className="hidden"
            onChange={(e) => handleFiles(e.target.files)}
          />
        </div>
      ) : (
        /* --- EMPTY STATE --- */
        <div
          className="flex flex-col items-center gap-[8px] justify-center h-full min-h-[80px]"
          onClick={() => fileInputRef.current?.click()}
        >
          {isLoading && <LoaderCircle className="animate-spin w-8 h-8" />}
          {!isLoading && (
            <>
              <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
                <rect x="0.5" y="0.5" width="31" height="31" rx="15.5" stroke="#555555" />
                <path
                  d="M22 18V20.6667C22 21.0203 21.8595 21.3594 21.6095 21.6095C21.3594 21.8595 21.0203 22 20.6667 22H11.3333C10.9797 22 10.6406 21.8595 10.3905 21.6095C10.1405 21.3594 10 21.0203 10 20.6667V18"
                  stroke="#555555"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path d="M19.3332 13.3333L15.9998 10L12.6665 13.3333" stroke="#555555" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M16 10V18" stroke="#555555" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              <p className="text-center">Click or drop files to upload</p>
            </>
          )}

          {/* Hidden input */}
          <input
            ref={fileInputRef}
            type="file"
            multiple
            className="hidden"
            onChange={(e) => handleFiles(e.target.files)}
          />
        </div>
      )}
    </div>
  );
};

export default MediaUploader;
